import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { Heart, PackageX, Share2, Star, Minus, Plus, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { toast } from "sonner";
import { useProduct } from "../features/catalog/api";
import { useProductReviews, useSubmitReview } from "../features/reviews/api";
import { formatPrice } from "../lib/format";
import { getImageVariants } from "../lib/imageVariants";
import useCartStore from "../store/cartStore";
import useWishlistStore from "../store/wishlistStore";
import SCSpinner from "../components/loaders/SCSpinner";
import Breadcrumbs from "../components/ui/Breadcrumbs";
import useSEO from "../hooks/useSEO";

export default function ProductDetail() {
  const { slug } = useParams();
  const { data: product, isLoading, isError } = useProduct(slug);

  if (isError) {
    return <ProductNotFound />;
  }

  if (isLoading || !product) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center">
        <SCSpinner size={64} />
      </div>
    );
  }
  return <ProductDetailLoaded product={product} />;
}

function ProductNotFound() {
  useSEO({ title: "Product not found", noindex: true });
  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center px-6 text-center">
      <PackageX size={40} className="text-mono/15" />
      <h1 className="mt-4 font-serif text-xl text-ink">Product not found</h1>
      <p className="mt-2 text-sm text-ink-light">
        This product may have been removed or the link is incorrect.
      </p>
      <Link
        to="/categories"
        className="mt-6 rounded-2xl bg-[#2b2b2d] px-8 py-3 text-xs tracking-wide text-white shadow-floating dark:bg-gold-500 dark:text-ink-dark dark:shadow-gold"
      >
        BROWSE PRODUCTS
      </Link>
    </div>
  );
}

function ProductDetailLoaded({ product }) {
  useSEO({
    title: product.name,
    description: product.description || undefined,
  });
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  const addItem = useCartStore((s) => s.addItem);
  const inWishlist = useWishlistStore((s) => s.has(product.id));
  const toggleWishlist = useWishlistStore((s) => s.toggle);

  // product.rating/reviewCount are computed server-side from this same
  // approved-reviews data (see productsController.js's attachReviewStats)
  // — reviewCount is 0 and rating is null for a product with no reviews
  // yet, which the JSX below treats as "hide the rating row" rather than
  // faking a 0-star product.
  const { data: productReviews = [] } = useProductReviews(product.slug);
  const displayRating = product.rating;
  const displayReviewCount = product.reviewCount;

  // A product can carry several independent variant groups at once —
  // Color, Wattage, Voltage, Length/Meter, etc. — not just one. Group
  // them by name, then track one selection PER group rather than a
  // single selected variant, so any combination is possible.
  const variantGroups = (product.variants || []).reduce((acc, v) => {
    (acc[v.name] ||= []).push(v);
    return acc;
  }, {});

  const [selectedOptions, setSelectedOptions] = useState(() => {
    const initial = {};
    for (const [groupName, options] of Object.entries(variantGroups)) {
      initial[groupName] = options[0];
    }
    return initial;
  });

  const selectedList = Object.values(selectedOptions);
  const unitPrice = product.price + selectedList.reduce((sum, o) => sum + (o?.priceDelta || 0), 0);

  function handleAddToCart() {
    addItem(product, selectedList, quantity);
    toast.success(`Added ${product.name} to cart`, {
      description: selectedList.length
        ? selectedList.map((o) => `${o.name}: ${o.value}`).join(" · ")
        : undefined,
    });
  }

  function handleShare() {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: product.name, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url);
      toast.info("Link copied to clipboard");
    }
  }

  return (
    <div className="pb-6 lg:pb-12">
      <div className="px-4 pt-4 lg:px-6">
        <Breadcrumbs
          items={[
            { label: product.category.name, to: `/category/${product.category.slug}` },
            { label: product.name },
          ]}
        />
      </div>

      <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:px-6">
        {/* Gallery column — sticks in place while the info column scrolls
            past it on desktop, same as any standard desktop product page. */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          {/* ----------------------------------------------------------
              Interactive image viewer — pinch/double-tap to zoom on
              touch, double-click or the +/- controls on desktop. Plain
              wheel scroll is intentionally left alone (activationKeys
              requires Ctrl/Cmd) so it scrolls the page like everywhere
              else, not the image — that's what was trapping desktop
              scroll before.
          ---------------------------------------------------------- */}
          <div className="relative mx-4 mt-3 aspect-square overflow-hidden rounded-3xl bg-mono/[0.03] lg:mx-0 lg:mt-0">
            <TransformWrapper
              key={activeImage}
              initialScale={1}
              minScale={1}
              maxScale={4}
              doubleClick={{ mode: "toggle" }}
              wheel={{ step: 0.15, activationKeys: ["Control", "Meta"] }}
            >
              {({ zoomIn, zoomOut, resetTransform }) => (
                <>
                  <TransformComponent wrapperStyle={{ width: "100%", height: "100%" }}>
                    {/* Full resolution, not lazy — this is the primary
                        above-the-fold image and the whole point of the
                        zoom viewer is detail, so a downscaled variant
                        would look soft once zoomed in. */}
                    <img
                      src={product.images[activeImage]?.url}
                      width={1600}
                      height={1600}
                      alt={product.name}
                      className="h-full w-full object-cover"
                      draggable={false}
                    />
                  </TransformComponent>

                  {/* Discoverable desktop zoom controls — there's no pinch
                      gesture with a mouse, so this is the primary way
                      desktop users zoom in. */}
                  <div className="absolute bottom-3 right-3 hidden flex-col gap-1.5 lg:flex">
                    <button
                      type="button"
                      aria-label="Zoom in"
                      onClick={() => zoomIn()}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-ink-light shadow-sm backdrop-blur-sm hover:text-ink"
                    >
                      <ZoomIn size={15} />
                    </button>
                    <button
                      type="button"
                      aria-label="Zoom out"
                      onClick={() => zoomOut()}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-ink-light shadow-sm backdrop-blur-sm hover:text-ink"
                    >
                      <ZoomOut size={15} />
                    </button>
                    <button
                      type="button"
                      aria-label="Reset zoom"
                      onClick={() => resetTransform()}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-ink-light shadow-sm backdrop-blur-sm hover:text-ink"
                    >
                      <RotateCcw size={13} />
                    </button>
                  </div>
                </>
              )}
            </TransformWrapper>

            <span className="pointer-events-none absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-black/40 px-3 py-1.5 text-[11px] text-white lg:hidden">
              <ZoomIn size={12} /> Pinch or double-tap to zoom
            </span>

            <div className="absolute right-3 top-3 flex flex-col gap-2">
              <button
                type="button"
                aria-label={inWishlist ? "Remove from wishlist" : "Add to wishlist"}
                onClick={() => toggleWishlist(product.id)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-sm backdrop-blur-sm active:scale-90"
              >
                <Heart size={17} className={inWishlist ? "fill-gold-500 text-gold-500" : "text-ink-light"} />
              </button>
              <button
                type="button"
                aria-label="Share"
                onClick={handleShare}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-sm backdrop-blur-sm active:scale-90"
              >
                <Share2 size={16} className="text-ink-light" />
              </button>
            </div>
          </div>

          {/* Products carry at most 3 photos, so this always stays a
              single short row — no need for a scroll-heavy gallery. */}
          {product.images.length > 1 && (
            <div className="flex gap-2 px-4 py-3 lg:px-0">
              {product.images.map((img, i) => (
                <button
                  key={img.id}
                  onClick={() => setActiveImage(i)}
                  className={`h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 lg:h-20 lg:w-20 ${
                    i === activeImage ? "border-gold-500" : "border-transparent"
                  }`}
                >
                  <img
                    src={getImageVariants(img.url).thumb}
                    width={80}
                    height={80}
                    loading="lazy"
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info column */}
        <div className="px-4 pt-2 lg:px-0 lg:pt-0">
          <h1 className="font-serif text-2xl text-ink lg:mt-1 lg:text-3xl">{product.name}</h1>

          {displayReviewCount > 0 && (
            <div className="mt-1.5 flex items-center gap-1.5 text-sm text-ink-light">
              <Star size={14} className="fill-gold-500 text-gold-500" />
              {displayRating.toFixed(1)}
              <span className="text-mono/30">({displayReviewCount} reviews)</span>
            </div>
          )}

          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-2xl text-ink">{formatPrice(unitPrice)}</span>
            {product.compareAtPrice && (
              <span className="text-sm text-ink-light line-through">
                {formatPrice(product.compareAtPrice)}
              </span>
            )}
          </div>

          {Object.entries(variantGroups).map(([groupName, options]) => (
            <div key={groupName} className="mt-5">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-light">
                {groupName}
                {selectedOptions[groupName] && (
                  <span className="ml-1.5 normal-case text-ink">— {selectedOptions[groupName].value}</span>
                )}
              </p>
              <div className="flex flex-wrap gap-2">
                {options.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setSelectedOptions((prev) => ({ ...prev, [groupName]: opt }))}
                    className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
                      selectedOptions[groupName]?.id === opt.id
                        ? "border-gold-500 bg-gold-50 text-ink dark:bg-gold-500/15 dark:text-gold-200"
                        : "border-mono/10 text-ink-light"
                    }`}
                  >
                    {opt.value}
                    {opt.priceDelta ? (
                      <span className="ml-1 text-xs text-ink-light">
                        ({opt.priceDelta > 0 ? "+" : ""}
                        {formatPrice(opt.priceDelta)})
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div className="mt-5 flex items-center gap-3">
            <p className="text-xs font-medium uppercase tracking-wide text-ink-light">Qty</p>
            <div className="flex items-center rounded-full border border-mono/10">
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="flex h-9 w-9 items-center justify-center text-ink-light"
                aria-label="Decrease quantity"
              >
                <Minus size={14} />
              </button>
              <span className="w-6 text-center text-sm">{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity((q) => q + 1)}
                className="flex h-9 w-9 items-center justify-center text-ink-light"
                aria-label="Increase quantity"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>

          <motion.button
            type="button"
            onClick={handleAddToCart}
            whileTap={{ scale: 0.97 }}
            className="mt-6 w-full rounded-2xl bg-[#2b2b2d] py-3.5 text-sm tracking-wide text-white shadow-floating dark:bg-gold-500 dark:text-ink-dark dark:shadow-gold lg:max-w-sm"
          >
            Add to Cart — {formatPrice(unitPrice * quantity)}
          </motion.button>

          <div className="mt-8 border-t border-mono/5 pt-6">
            <h2 className="mb-2 font-serif text-lg text-ink">Description</h2>
            <p className="text-sm leading-relaxed text-ink-light">{product.description}</p>
          </div>

          <SpecificationsTable specifications={product.specifications} />

          <ReviewsSection product={product} reviews={productReviews} reviewCount={displayReviewCount} />
        </div>
      </div>
    </div>
  );
}

function SpecificationsTable({ specifications }) {
  if (!specifications || specifications.length === 0) return null;
  return (
    <div className="mt-8 border-t border-mono/5 pt-6">
      <h2 className="mb-3 font-serif text-lg text-ink">Specifications</h2>
      <dl className="divide-y divide-mono/5 overflow-hidden rounded-2xl border border-mono/5">
        {specifications.map((spec) => (
          <div key={spec.key} className="flex justify-between gap-4 px-4 py-2.5 text-sm odd:bg-mono/[0.015]">
            <dt className="text-ink-light">{spec.key}</dt>
            <dd className="text-right text-ink">{spec.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function ReviewsSection({ product, reviews, reviewCount }) {
  const submitReview = useSubmitReview();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !comment.trim() || rating === 0) {
      toast.error("Add your name, a rating, and a comment.");
      return;
    }
    try {
      await submitReview.mutateAsync({
        productSlug: product.slug,
        guestName: name.trim(),
        rating,
        comment: comment.trim(),
      });
      toast.success("Thanks! Your review will appear once it's approved.");
      setName("");
      setRating(0);
      setComment("");
      setShowForm(false);
    } catch {
      toast.error("Couldn't submit your review — please try again.");
    }
  }

  return (
    <div className="mt-8 border-t border-mono/5 pt-6">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-serif text-lg text-ink">Reviews</h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-ink-light">{reviewCount} total</span>
          <button
            type="button"
            onClick={() => setShowForm((s) => !s)}
            className="text-xs font-medium text-gold-600 dark:text-gold-400"
          >
            {showForm ? "Cancel" : "Write a review"}
          </button>
        </div>
      </div>

      {showForm && (
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mb-4 space-y-3 overflow-hidden rounded-2xl bg-surface p-3.5 shadow-card"
        >
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="checkout-input"
          />
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <button key={s} type="button" onClick={() => setRating(s)} aria-label={`${s} stars`}>
                <Star size={20} className={s <= rating ? "fill-gold-500 text-gold-500" : "text-mono/20"} />
              </button>
            ))}
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            placeholder="What did you think?"
            className="checkout-input resize-none"
          />
          <button
            type="submit"
            className="rounded-xl bg-[#2b2b2d] px-5 py-2.5 text-xs tracking-wide text-white dark:bg-gold-500 dark:text-ink-dark"
          >
            Submit review
          </button>
        </motion.form>
      )}

      {reviews.length === 0 ? (
        <p className="text-sm text-ink-light">No reviews yet — be the first.</p>
      ) : (
        <AnimatePresence>
          <div className="space-y-4">
            {reviews.map((r, i) => (
              <motion.div
                key={r.uuid}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06, duration: 0.25 }}
                className="rounded-2xl bg-surface p-3.5 shadow-card"
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm text-ink">{r.guestName}</p>
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: r.rating }).map((_, s) => (
                      <Star key={s} size={12} className="fill-gold-500 text-gold-500" />
                    ))}
                  </div>
                </div>
                <p className="mt-1 text-sm text-ink-light">{r.comment}</p>
              </motion.div>
            ))}
          </div>
        </AnimatePresence>
      )}
    </div>
  );
}
